import { Button } from '@civilgrid/ui/components';
import { useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useSnapshot } from 'valtio';
import { addFile as apiAddFile } from '~/api/organizations2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Notify from '~/components2/Notify';
import rootStore from '~/store';

interface FileUploadProps {
  loadLogo: () => void;
}

export const FileUpload = ({ loadLogo }: FileUploadProps) => {
  const type = 'media';
  const imageExts = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'tif', 'tiff', 'eps', 'raw', 'cr2', 'nef', 'orf', 'sr2'];
  const [isUploading, setIsUploading] = useState(false);
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: { 'image/*': imageExts.map((ext) => `.${ext}`) },
    onDrop,
    maxSize: 1073741824, // max file size 1 GB
    noClick: true,
  });

  async function sendFile(file: File) {
    const data = new FormData();
    data.append('file', file);
    data.append('name', file.name);
    data.append('type', type);
    data.append('extension', file.name.split('.').pop()!);

    try {
      return await apiAddFile(currentOrganizationId, data);
    } catch (e) {
      console.error(e);
    }
  }

  async function uploadFiles(files: File[]) {
    const uploadRequests = files.map((file) => sendFile(file));

    return Promise.all(uploadRequests).then(() => {
      loadLogo();
    });
  }

  function onDrop(accepted: File[], rejected: FileRejection[]) {
    // collect and display error messages (mostly wrong file types)
    let uploadErrors: { message?: string; files?: string[] } = {};
    rejected.forEach(({ file, errors }) => {
      errors.forEach(({ code, message }) => {
        let newMessage = message;
        if (code === 'file-invalid-type') {
          newMessage = 'Files must be a video or image.';
        } else if (code === 'file-too-large') {
          newMessage = 'File is larger than 1 GB.';
        }

        // only show each error once, and batch affected files
        if (uploadErrors.message !== code) {
          uploadErrors = {
            message: newMessage,
            files: [],
          };
        }
        uploadErrors.files?.push(file.name);
      });
    });
    if (Object.values(uploadErrors).length > 0) {
      Object.values(uploadErrors).forEach((value) => {
        Notify.warning({
          title: '⚠️',
          message: `<div>${value[0]} The following files were affected: <b>${(value as string[])
            .slice(1)
            .join(', ')}</b></div>`,
        });
      });
    }
    setIsUploading(true);
    uploadFiles(accepted)
      .then(() => {
        setIsUploading(false);
        capturePosthogEvent(POSTHOG_EVENTS.organizationLogoUploaded, {
          type,
          organization_id: currentOrganizationId,
        });
      })
      .catch((err: any) => {
        console.error(err);
        Notify.error({
          title: '⚠️',
          message: 'Error uploading files',
        });
      });
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <Button disabled={isUploading} onClick={open}>
        Upload File
      </Button>
    </div>
  );
};
